import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { companyAPI } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';

const PAGES = [
  { key: 'home', label: 'Home', path: '/' },
  { key: 'services', label: 'Services', path: '/services' },
  { key: 'about', label: 'About', path: '/about' },
  { key: 'quote', label: 'Quote', path: '/quote' },
  { key: 'contact', label: 'Contact', path: '/contact' },
];

const emptyPage = { title: '', description: '', keywords: '', og_image: '', noindex: false };

export const SeoManager = () => {
  const { toast } = useToast();
  const [seo, setSeo] = useState({ canonical_base: '', default_og_image: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    companyAPI.getInfo().then((res) => {
      setSeo({ canonical_base: '', default_og_image: '', ...(res.data.seo || {}) });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPage = (k) => seo[k] || emptyPage;
  const setPageField = (k, field, value) =>
    setSeo((s) => ({ ...s, [k]: { ...emptyPage, ...(s[k] || {}), [field]: value } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAPI.updateInfo({ seo });
      toast({ title: 'Success', description: 'SEO settings updated' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save SEO settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-semibold">Global SEO</h3>
          <p className="text-sm text-gray-500">Leave blank to use defaults. Empty per-page fields fall back to the built-in defaults.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Canonical Base Domain</Label>
              <Input value={seo.canonical_base || ''} placeholder="https://ecowaycabs.com" onChange={(e) => setSeo({ ...seo, canonical_base: e.target.value })} data-testid="seo-canonical-base-input" />
            </div>
            <div>
              <Label>Default Social Share Image URL</Label>
              <Input value={seo.default_og_image || ''} placeholder="https://.../share.png" onChange={(e) => setSeo({ ...seo, default_og_image: e.target.value })} data-testid="seo-default-og-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {PAGES.map((p) => {
        const pg = getPage(p.key);
        return (
          <Card key={p.key}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{p.label} <span className="text-xs text-gray-400 font-normal">({p.path})</span></h3>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id={`noindex-${p.key}`} checked={!!pg.noindex} onChange={(e) => setPageField(p.key, 'noindex', e.target.checked)} className="h-4 w-4" data-testid={`seo-${p.key}-noindex-switch`} />
                  <Label htmlFor={`noindex-${p.key}`} className="text-sm">No-index this page</Label>
                </div>
              </div>
              <div>
                <Label>Meta Title</Label>
                <Input value={pg.title} onChange={(e) => setPageField(p.key, 'title', e.target.value)} data-testid={`seo-${p.key}-title-input`} />
              </div>
              <div>
                <Label>Meta Description</Label>
                <Textarea rows={2} value={pg.description} onChange={(e) => setPageField(p.key, 'description', e.target.value)} data-testid={`seo-${p.key}-description-input`} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Meta Keywords</Label>
                  <Input value={pg.keywords} onChange={(e) => setPageField(p.key, 'keywords', e.target.value)} data-testid={`seo-${p.key}-keywords-input`} />
                </div>
                <div>
                  <Label>Social Share Image URL</Label>
                  <Input value={pg.og_image} onChange={(e) => setPageField(p.key, 'og_image', e.target.value)} placeholder="Optional, overrides default" data-testid={`seo-${p.key}-ogimage-input`} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Button onClick={handleSave} disabled={saving} data-testid="seo-save-button">
        {saving ? 'Saving...' : 'Save SEO Settings'}
      </Button>
    </div>
  );
};

export default SeoManager;
